import type { FC, ReactNode } from 'react'

import { BrainCircuit, Radar, ShieldCheck, Waves } from 'lucide-react'
import { Wordmark } from '@/components/brand/wordmark'

interface AuthLayoutProps {
    eyebrow: string
    title: ReactNode
    description: string
    children: ReactNode
}

const HIGHLIGHTS = [
    { icon: BrainCircuit, title: 'Bàn phân tích 7 vai trò', text: 'Kỹ thuật, cơ bản, dòng tiền, vĩ mô, tin tức, rủi ro và một kết luận tổng hợp cho từng mã.' },
    { icon: Waves, title: 'Bản đồ sóng ngành', text: 'Thấy ngành nào vừa khởi sóng trước khi dòng tiền đổ vào.' },
    { icon: Radar, title: 'Radar mua sớm', text: 'Lọc mã sát điểm pivot, vừa vượt nền hoặc vừa đảo chiều.' },
    { icon: ShieldCheck, title: 'Kế hoạch có rủi ro rõ ràng', text: 'Vùng mua, điểm dừng lỗ, mục tiêu và tỷ trọng gợi ý.' }
]

export const AuthLayout: FC<AuthLayoutProps> = ({ eyebrow, title, description, children }): ReactNode => {
    return (
        <div className='glow-backdrop grid min-h-screen bg-background lg:grid-cols-[1.05fr_0.95fr]'>
            <div className='relative hidden flex-col justify-between overflow-hidden border-r border-line px-12 py-10 lg:flex xl:px-16'>
                <div aria-hidden='true' className='pointer-events-none absolute -right-40 -top-28 size-[560px] rounded-full border border-lime/10 shadow-[0_0_0_70px_rgba(212,244,90,0.02),0_0_0_140px_rgba(212,244,90,0.02)]' />

                <Wordmark />

                <div className='relative max-w-xl'>
                    <span className='font-mono text-[10px] uppercase tracking-[0.16em] text-lime-soft'>Horse Moon / Investment intelligence</span>

                    <p className='mt-6 font-heading text-[clamp(2.6rem,4.4vw,4.4rem)] font-medium leading-[1.0] tracking-[-0.07em] text-foreground'>
                        Đọc thị trường <span className='text-lime'>trước khi</span> nó chạy.
                    </p>

                    <ul className='mt-10 grid gap-5 sm:grid-cols-2'>
                        {HIGHLIGHTS.map(({ icon: Icon, title: heading, text }) => (
                            <li key={heading} className='border-t border-line pt-4'>
                                <Icon className='size-5 text-lime' aria-hidden='true' />
                                <p className='mt-3 font-heading text-sm font-medium text-foreground'>{heading}</p>
                                <p className='mt-1.5 text-xs leading-relaxed text-muted-foreground'>{text}</p>
                            </li>
                        ))}
                    </ul>
                </div>

                <p className='relative font-mono text-[10px] text-subtle'>Dữ liệu mô phỏng phục vụ minh họa, không phải khuyến nghị đầu tư.</p>
            </div>

            <main className='flex flex-col px-5 py-8 sm:px-10 lg:justify-center lg:px-16'>
                <div className='lg:hidden'>
                    <Wordmark />
                </div>

                <div className='mx-auto mt-12 w-full max-w-md lg:mt-0'>
                    <span className='font-mono text-[10px] uppercase tracking-[0.16em] text-lime-soft'>{eyebrow}</span>

                    <h1 className='mt-4 font-heading text-4xl font-medium leading-[1.05] tracking-[-0.06em] text-foreground [&_em]:not-italic [&_em]:text-lime'>
                        {title}
                    </h1>

                    <p className='mt-3 text-sm leading-relaxed text-muted-foreground'>{description}</p>

                    <div className='mt-8'>{children}</div>
                </div>
            </main>
        </div>
    )
}
